import React, { useEffect, useState } from 'react';
import { Modal, Tabs, Typography } from 'antd';
import { EnhancedViewer } from '@partsbadger/threedviewer';

export interface CompareParty {
    name: string;
    step_file_3djs_url?: string | null;
    step_file_url?: string | null;
    drawing_file_url?: string | null;
    dimensions?: string | null;
}

interface ComparePartsModalProps {
    open: boolean;
    onClose: () => void;
    current: CompareParty | null;
    matched: CompareParty | null;
}

const SIDE_HEIGHT = 640;

const SidePanel: React.FC<{
    label: string;
    party: CompareParty | null;
    tab: '3d' | '2d';
    active: boolean;
}> = ({ label, party, tab, active }) => {
    const threeDUrl = party?.step_file_3djs_url || null;
    const rawStepUrl = party?.step_file_url || null;
    const drawingUrl = party?.drawing_file_url || null;

    return (
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
            <div
                style={{
                    padding: '6px 10px',
                    background: '#fafafa',
                    borderBottom: '1px solid #eee',
                    fontSize: 12,
                }}
            >
                <span style={{ color: '#888', marginRight: 6 }}>{label}:</span>
                <span style={{ fontWeight: 600 }}>{party?.name || '—'}</span>
                {party?.dimensions && (
                    <span style={{ color: '#888', marginLeft: 8, fontWeight: 400 }}>{party.dimensions}</span>
                )}
            </div>
            <div
                style={{
                    width: '100%',
                    height: SIDE_HEIGHT,
                    background: '#f0f0f0',
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                {tab === '3d' ? (
                    threeDUrl && active ? (
                        <EnhancedViewer key={`${label}-${threeDUrl}`} url={threeDUrl} />
                    ) : (
                        <div
                            style={{
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                textAlign: 'center',
                                color: '#999',
                                padding: 16,
                            }}
                        >
                            <div>
                                <div>3D preview not available</div>
                                {rawStepUrl && (
                                    <Typography.Link
                                        href={rawStepUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ fontSize: 12 }}
                                    >
                                        Download STEP file
                                    </Typography.Link>
                                )}
                            </div>
                        </div>
                    )
                ) : drawingUrl ? (
                    <iframe
                        title={`${label} 2D drawing`}
                        src={drawingUrl}
                        style={{ width: '100%', height: '100%', border: 0, background: '#fff' }}
                    />
                ) : (
                    <div style={{ textAlign: 'center', color: '#999', padding: 16 }}>No 2D drawing available</div>
                )}
            </div>
        </div>
    );
};

const ComparePartsModal: React.FC<ComparePartsModalProps> = ({ open, onClose, current, matched }) => {
    const [activeTab, setActiveTab] = useState<'3d' | '2d'>('3d');

    useEffect(() => {
        if (open) setActiveTab('3d');
    }, [open]);

    const titleCurrent = current?.name ? ` "${current.name}"` : '';
    const titleMatched = matched?.name ? ` "${matched.name}"` : '';

    return (
        <Modal
            title={
                <span>
                    Compare{titleCurrent} vs{titleMatched}
                </span>
            }
            open={open}
            onCancel={onClose}
            footer={null}
            width="95vw"
            style={{ maxWidth: 1600, top: 20 }}
            bodyStyle={{ padding: 12 }}
            destroyOnClose
        >
            <Tabs
                activeKey={activeTab}
                onChange={key => setActiveTab(key as '3d' | '2d')}
                items={[
                    {
                        key: '3d',
                        label: '3D Model',
                        children: (
                            <div style={{ display: 'flex', gap: 12 }}>
                                <SidePanel
                                    label="Current"
                                    party={current}
                                    tab="3d"
                                    active={open && activeTab === '3d'}
                                />
                                <SidePanel
                                    label="Matched"
                                    party={matched}
                                    tab="3d"
                                    active={open && activeTab === '3d'}
                                />
                            </div>
                        ),
                    },
                    {
                        key: '2d',
                        label: '2D Drawing',
                        children: (
                            <div style={{ display: 'flex', gap: 12 }}>
                                <SidePanel
                                    label="Current"
                                    party={current}
                                    tab="2d"
                                    active={open && activeTab === '2d'}
                                />
                                <SidePanel
                                    label="Matched"
                                    party={matched}
                                    tab="2d"
                                    active={open && activeTab === '2d'}
                                />
                            </div>
                        ),
                    },
                ]}
            />
        </Modal>
    );
};

export default ComparePartsModal;
